// SafetyAudit.js
// This file contains the SafetyAudit data model along with helper models that mirror the MySQL helper tables

import { v4 as uuidv4 } from "uuid";

import { validateSafetyAuditChecklist } from "../utils/safetyAuditValidation";
import { FormPhotos } from "../../FormPhotos/FormPhotos";
import { JsaV1, JsaV2 } from "../../Jsa";

export class SafetyAuditChecklistEmployees {
  constructor(data = {}) {
    this.id = data.id || uuidv4();
    this.safety_audit_checklist_id = data.safety_audit_checklist_id || null;
    this.employee_id = data.employee_id || null;
    this.employee_name = data.employee_name || null;
  }

  updateSafetyAuditChecklistId(safetyAuditChecklistId) {
    this.safety_audit_checklist_id = safetyAuditChecklistId;
  }

  selectEmployee(selectedEmployee) {
    if (!selectedEmployee) {
      this.employee_id = null;
      this.employee_name = null;
      return;
    }
    this.employee_id = selectedEmployee.value;
    this.employee_name = selectedEmployee.label;
  }

  toJson() {
    return {
      id: this.id,
      safety_audit_checklist_id: this.safety_audit_checklist_id,
      employee_id: this.employee_id,
    };
  }

  // Helper for employee patches to be sent via websocket
  toPatch(action, fields = {}) {
    const patch = {
      patch_type: "safety_audit_employee",
      action: action,
      id: this.id,
    };

    // For add/update actions, include the fields
    if (action === "add" || action === "update") {
      if (fields.employee_id !== undefined) {
        patch.employee_id = fields.employee_id;
      } else if (this.employee_id) {
        patch.employee_id = this.employee_id;
      }
    }

    return patch;
  }
}

export class SafetyAuditChecklist {
  constructor(data = {}) {
    this.id = data.id || null;
    this.audit_date = data.audit_date || null;
    this.audit_time = data.audit_time || null;
    this.project_number = data.project_number || null;
    this.project_name = data.project_name || null;
    this.supervisor = data.supervisor || null;
    this.supervisor_name = data.supervisor_name
      ? data.supervisor + " - " + data.supervisor_name
      : null;
    this.scope_of_work = data.scope_of_work || null;
    this.pw_jsa = data.pw_jsa || false;
    this.pw_field_notes = data.pw_field_notes || false;
    this.pw_comments = data.pw_comments || null;
    this.jsa_signed = data.jsa_signed || false;
    this.equipment_on_site = data.equipment_on_site || null;
    this.hazards_present = data.hazards_present || null;
    this.hazards_on_jsa_yes = data.hazards_on_jsa_yes || false;
    this.hazards_on_jsa_no = data.hazards_on_jsa_no || false;
    this.engineering_controls = data.engineering_controls || null;
    this.ppe_used = data.ppe_used || null;
    this.correct_ppe_yes = data.correct_ppe_yes || false;
    this.correct_ppe_no = data.correct_ppe_no || false;
    this.properly_trained_yes = data.properly_trained_yes || false;
    this.properly_trained_no = data.properly_trained_no || false;
    this.properly_trained_comments = data.properly_trained_comments || null;
    this.certs_obtained = data.certs_obtained || null;
    this.positive_findings = data.positive_findings || null;
    this.negative_findings = data.negative_findings || null;
    this.created_at = data.created_at || null;
    this.updated_at = data.updated_at || null;
    this.employees = data.employees
      ? data.employees.map(
          (employee) =>
            new SafetyAuditChecklistEmployees({
              id: employee.id,
              employee_id: employee.employee_id,
              employee_name: employee.employee_name,
              safety_audit_checklist_id: this.id,
            })
        )
      : [];
    this.status = data.status || "Draft";

    // DEL Association fields
    this.del_id = data.del_id || null;
    this.jsa_id = data.jsa_id || null;
    this.jsa_version = data.jsa_version || null;

    // JSA data (lazy-loaded, not from initial API response)
    this.jsa = null;
    this.jsaLoaded = false;

    // Photos array - populated with presigned URLs from backend
    this.photos = data.photos
      ? data.photos.map((photo) => new FormPhotos(photo))
      : [];

    this.errors = data.errors || null;
  }

  updateSafetyAuditChecklistId(safetyAuditChecklistId) {
    this.id = safetyAuditChecklistId;
  }

  _formatDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  updateAuditDate(auditDate) {
    if (!auditDate) {
      this.audit_date = null;
      return;
    }
    this.audit_date = this._formatDate(auditDate);
  }

  updateAuditTime(auditTime) {
    this.audit_time = auditTime;
  }

  updateProjectNumber(project) {
    if (!project) {
      this.project_number = null;
      this.project_name = null;
      return;
    }
    this.project_number = project.value;
    this.project_name = project.project_name;
  }

  updateSupervisor(selectedSupervisor) {
    if (!selectedSupervisor) {
      this.supervisor = null;
      this.supervisor_name = null;
      return;
    }
    this.supervisor = selectedSupervisor.value;
    this.supervisor_name = selectedSupervisor.label;
  }

  updateScopeOfWork(scopeOfWork) {
    this.scope_of_work = scopeOfWork;
  }

  updatePwJsa(pwJsa) {
    this.pw_jsa = pwJsa;
  }

  updatePwFieldNotes(pwFieldNotes) {
    this.pw_field_notes = pwFieldNotes;
  }

  updatePwComments(pwComments) {
    this.pw_comments = pwComments;
  }

  updateJsaSigned(jsaSigned) {
    this.jsa_signed = jsaSigned;
  }

  updateEquipmentOnSite(equipmentOnSite) {
    this.equipment_on_site = equipmentOnSite;
  }

  updateHazardsPresent(hazardsPresent) {
    this.hazards_present = hazardsPresent;
  }

  updateHazardsOnJsaYes(hazardsOnJsaYes) {
    if (!hazardsOnJsaYes) {
      this.hazards_on_jsa_yes = false;
      return;
    } else {
      this.hazards_on_jsa_yes = hazardsOnJsaYes;
      this.hazards_on_jsa_no = false;
    }
  }

  updateHazardsOnJsaNo(hazardsOnJsaNo) {
    if (!hazardsOnJsaNo) {
      this.hazards_on_jsa_no = false;
      return;
    } else {
      this.hazards_on_jsa_no = hazardsOnJsaNo;
      this.hazards_on_jsa_yes = false;
    }
  }

  updateEngineeringControls(engineeringControls) {
    this.engineering_controls = engineeringControls;
  }

  updatePpeUsed(ppeUsed) {
    this.ppe_used = ppeUsed;
  }

  updateCorrectPpeYes(correctPpeYes) {
    if (!correctPpeYes) {
      this.correct_ppe_yes = false;
      return;
    } else {
      this.correct_ppe_yes = correctPpeYes;
      this.correct_ppe_no = false;
    }
  }

  updateCorrectPpeNo(correctPpeNo) {
    if (!correctPpeNo) {
      this.correct_ppe_no = false;
      return;
    } else {
      this.correct_ppe_no = correctPpeNo;
      this.correct_ppe_yes = false;
    }
  }

  updateProperlyTrainedYes(properlyTrainedYes) {
    if (!properlyTrainedYes) {
      this.properly_trained_yes = false;
      return;
    } else {
      this.properly_trained_yes = properlyTrainedYes;
      this.properly_trained_no = false;
    }
  }

  updateProperlyTrainedNo(properlyTrainedNo) {
    if (!properlyTrainedNo) {
      this.properly_trained_no = false;
      return;
    } else {
      this.properly_trained_no = properlyTrainedNo;
      this.properly_trained_yes = false;
    }
  }

  updateProperlyTrainedComments(properlyTrainedComments) {
    this.properly_trained_comments = properlyTrainedComments;
  }

  updateCertsObtained(certsObtained) {
    this.certs_obtained = certsObtained;
  }

  updatePositiveFindings(positiveFindings) {
    this.positive_findings = positiveFindings;
  }

  updateNegativeFindings(negativeFindings) {
    this.negative_findings = negativeFindings;
  }

  // DEL Association methods
  applyDelAssociation(associationData) {
    this.del_id = associationData.del_id;
    this.jsa_id = associationData.jsa_id;
    this.jsa_version = associationData.jsa_version;
    this.project_number = associationData.project_number;
    this.project_name = associationData.project_name;

    // Add new employees (already filtered for duplicates by backend)
    if (associationData.new_employees) {
      for (const emp of associationData.new_employees) {
        const newEmployee = new SafetyAuditChecklistEmployees({
          id: emp.id,
          safety_audit_checklist_id: this.id,
          employee_id: emp.employee_id,
          employee_name: emp.employee_name,
        });
        this.employees.push(newEmployee);
      }
    }

    // Reset JSA loaded state since we have a new association
    this.jsa = null;
    this.jsaLoaded = false;
  }

  clearDelAssociation() {
    this.del_id = null;
    this.jsa_id = null;
    this.jsa_version = null;
    this.project_number = null;
    this.project_name = null;
    this.employees = [];
    this.jsa = null;
    this.jsaLoaded = false;
  }

  setJsaData(jsaData, jsaVersion) {
    if (jsaVersion === 2) {
      this.jsa = new JsaV2(jsaData);
    } else {
      this.jsa = new JsaV1(jsaData);
    }
    this.jsaLoaded = true;
  }

  hasAssociatedDel() {
    return this.del_id !== null;
  }

  hasAssociatedJsa() {
    return this.jsa_id !== null;
  }

  addNewBlankEmployee() {
    this.employees.push(
      new SafetyAuditChecklistEmployees({
        safety_audit_checklist_id: this.id,
      })
    );
  }

  removeEmployee(rowId) {
    this.employees = this.employees.filter((employee) => employee.id !== rowId);
  }

  updateEmployee(rowId, employeeId, employeeName) {
    this.employees
      .find((employee) => employee.id === rowId)
      ?.selectEmployee(employeeId, employeeName);
    if (!employeeId) {
      this.employees
        .find((employee) => employee.id === rowId)
        ?.selectEmployee(null);
      return;
    }
    this.employees
      .find((employee) => employee.id === rowId)
      ?.selectEmployee(employeeId, employeeName);
  }

  // Helper function to check if the del data has validation errors,the power of recursion...
  hasValidationErrors(errors) {
    const checkErrors = (err) => {
      if (err == null) return false;
      if (typeof err === "string") {
        return err.trim() !== "";
      }
      if (Array.isArray(err)) {
        // Recursively check each item in the array
        return err.some((item) => checkErrors(item));
      }
      if (typeof err === "object") {
        // Recursively check all object keys
        return Object.keys(err).some((key) => checkErrors(err[key]));
      }
      return false;
    };

    return checkErrors(errors);
  }

  saveDraftSafetyAuditChecklist(userContext) {
    const safetyAuditChecklistData = {
      id: this.id,
      audit_date: this.audit_date,
      audit_time: this.audit_time,
      project_number: this.project_number,
      supervisor: this.supervisor,
      scope_of_work: this.scope_of_work,
      pw_jsa: this.pw_jsa,
      pw_field_notes: this.pw_field_notes,
      pw_comments: this.pw_comments,
      jsa_signed: this.jsa_signed,
      equipment_on_site: this.equipment_on_site,
      hazards_present: this.hazards_present,
      hazards_on_jsa_yes: this.hazards_on_jsa_yes,
      hazards_on_jsa_no: this.hazards_on_jsa_no,
      engineering_controls: this.engineering_controls,
      ppe_used: this.ppe_used,
      correct_ppe_yes: this.correct_ppe_yes,
      correct_ppe_no: this.correct_ppe_no,
      properly_trained_yes: this.properly_trained_yes,
      properly_trained_no: this.properly_trained_no,
      properly_trained_comments: this.properly_trained_comments,
      certs_obtained: this.certs_obtained,
      positive_findings: this.positive_findings,
      negative_findings: this.negative_findings,
      employees: this.employees.map((employee) => employee.toJson()),
      updated_by: userContext.employee_id,
      status: this.status,
    };
    console.log("Safety audit checklist data:", safetyAuditChecklistData);
    return safetyAuditChecklistData;
  }

  /**
   * Validate form for submission and return minimal payload.
   * Data is already saved via WebSocket autosave.
   */
  submitSafetyAuditSimple(userContext) {
    // Frontend validation for UX
    const { fieldErrors, notificationErrors } =
      validateSafetyAuditChecklist(this);
    if (this.hasValidationErrors(fieldErrors)) {
      this.errors = fieldErrors; // Set snake_case errors for form display
      return { errors: notificationErrors }; // Return nice errors for notification
    }
    this.errors = null;
    // Return minimal payload - data already persisted via autosave
    return { safety_audit_id: this.id };
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields) {
    const fieldMap = {
      auditDate: "audit_date",
      auditTime: "audit_time",
      projectNumber: "project_number",
      supervisor: "supervisor",
      scopeOfWork: "scope_of_work",
      pwJsa: "pw_jsa",
      pwFieldNotes: "pw_field_notes",
      pwComments: "pw_comments",
      jsaSigned: "jsa_signed",
      equipmentOnSite: "equipment_on_site",
      hazardsPresent: "hazards_present",
      hazardsOnJsaYes: "hazards_on_jsa_yes",
      hazardsOnJsaNo: "hazards_on_jsa_no",
      engineeringControls: "engineering_controls",
      ppeUsed: "ppe_used",
      correctPpeYes: "correct_ppe_yes",
      correctPpeNo: "correct_ppe_no",
      properlyTrainedYes: "properly_trained_yes",
      properlyTrainedNo: "properly_trained_no",
      properlyTrainedComments: "properly_trained_comments",
      certsObtained: "certs_obtained",
      positiveFindings: "positive_findings",
      negativeFindings: "negative_findings",
      status: "status",
    };

    const patch = { patch_type: "safety_audit_main" };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}
